// This is the model to credit conversion rates, we should only add new model to credit calculations, never change existing model
// calculations because we store the credit conversion in aggregated rows, meaning if we change the calculation mid-cycle, we will invalidate aggregated rows

import { z } from "zod";

import { OrgRole } from "../enums.js";

import { AnthropicModel, DeprecatedAnthropicModel } from "./anthropicTypes.js";
import { BillableModel, RawBillingQuantityDetails } from "./billingTypes.js";
import { KimiModel } from "./chatCompletionsTypes.js";
import { DeprecatedOpenAiChatModel, OpenAiChatModel } from "./openAiTypes.js";

export const FREE_TIER_USER_CREDITS = 10;

export type AiCreditLimit = number | "UNLIMITED";

/**
 * Sentinel value indicating unlimited AI credits.

 * Note: This should not be used in comparisons, instead use isUnlimitedAiCredits
 */
export const UNLIMITED_AI_CREDITS_SENTINEL = "UNLIMITED";

/**
 * Returns whether the given credit limit is unlimited.
 */
export const isUnlimitedAiCreditLimit = (
  credits: AiCreditLimit,
): credits is "UNLIMITED" => credits === UNLIMITED_AI_CREDITS_SENTINEL;

/**
 * Type for org-level AI credit limit overrides by role.
 * When set on an org, these values override the default USER_CREDITS_BY_FEATURE_TIER_AND_ROLE
 * for all users in that org with the specified role.
 * - null or undefined for a role: no override, use default from USER_CREDITS_BY_FEATURE_TIER_AND_ROLE
 * - UNLIMITED_AI_CREDITS_SENTINEL: unlimited credits for that role
 * - 0: completely disable AI features for that role
 * - positive number: specific credit limit for that role
 */
export type OrgAiCreditLimitsByRoleOverride = Partial<
  Record<OrgRole, AiCreditLimit | null>
>;

/**
 * Zod schema for validating OrgAiCreditLimitsByRoleOverride at runtime.
 */
const aiCreditLimitSchema = z.union([
  z.number().int().min(0),
  z.literal("UNLIMITED"),
  z.null(),
]);

export const OrgAiCreditLimitsByRoleOverrideSchema = z.record(
  z.nativeEnum(OrgRole),
  aiCreditLimitSchema.optional(),
);

/** An integer of millionth of credits, 1_000_000 millionth of a credit = 1 credit */
type MillionthCredits = number;

type CreditsFromUsageFn = (
  usage: RawBillingQuantityDetails,
) => MillionthCredits;

export interface LongContextPricing {
  inputTokenThreshold: number;
  inputCostMultiplier: number;
  outputCostMultiplier: number;
}

const sonnet4Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 3;
  const CACHE_WRITES_COST_PER_MILLION_TOKENS = 6;
  // times 10 to avoid fp math until the end
  const CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10 = 3;
  const OUTPUT_COST_PER_MILLION_TOKENS = 15;
  const { cacheWrites, cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheWritesMillionthCredits =
    cacheWrites * CACHE_WRITES_COST_PER_MILLION_TOKENS;
  const cachedInputMillionthCredits =
    (cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10) / 10;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // Sonnet4@08/2025: Billed at input: $3/MTok, 1hr cachedWrites: $6/MTok  cachedInput: $.30/MTok output: $15/MTok
  return (
    inputMillionthCredits +
    cacheWritesMillionthCredits +
    cachedInputMillionthCredits +
    outputMillionthCredits
  );
};

const haiku45Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 1;
  const CACHE_WRITES_COST_PER_MILLION_TOKENS = 2;
  // times 10 to avoid fp math until the end
  const CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10 = 1;
  const OUTPUT_COST_PER_MILLION_TOKENS = 5;

  const { cacheWrites, cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheWritesMillionthCredits =
    cacheWrites * CACHE_WRITES_COST_PER_MILLION_TOKENS;
  const cachedInputMillionthCredits =
    (cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10) / 10;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // Haiku4.5@10/2025: Billed at input: $1/MTok, 1hr cachedWrites: $2/MTok, cachedInput: $0.10/MTok, output: $5/MTok
  return (
    inputMillionthCredits +
    cacheWritesMillionthCredits +
    cachedInputMillionthCredits +
    outputMillionthCredits
  );
};

const opus4Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 15;
  const CACHE_WRITES_COST_PER_MILLION_TOKENS = 30;
  // times 10 to avoid fp math until the end
  const CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10 = 15;
  const OUTPUT_COST_PER_MILLION_TOKENS = 75;

  const { cacheWrites, cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheWritesMillionthCredits =
    cacheWrites * CACHE_WRITES_COST_PER_MILLION_TOKENS;
  const cachedInputMillionthCredits =
    (cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10) / 10;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // Opus4@08/2025: Billed at input: $15/MTok, 1hr cachedWrites: $30/MTok  cachedInput: $1.50/MTok output: $75/MTok
  return (
    inputMillionthCredits +
    cacheWritesMillionthCredits +
    cachedInputMillionthCredits +
    outputMillionthCredits
  );
};

const opus45Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 5;
  const CACHE_WRITES_COST_PER_MILLION_TOKENS = 10;
  // times 10 to avoid fp math until the end
  const CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10 = 5;
  const OUTPUT_COST_PER_MILLION_TOKENS = 25;

  const { cacheWrites, cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheWritesMillionthCredits =
    cacheWrites * CACHE_WRITES_COST_PER_MILLION_TOKENS;
  const cachedInputMillionthCredits =
    (cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10) / 10;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // Opus4.5@11/2025: Billed at input: $5/MTok, 1hr cachedWrites: $10/MTok  cachedInput: $0.50/MTok output: $25/MTok
  return (
    inputMillionthCredits +
    cacheWritesMillionthCredits +
    cachedInputMillionthCredits +
    outputMillionthCredits
  );
};

const LONG_CONTEXT_THRESHOLD = 200_000;
const OPENAI_GPT_55_LONG_CONTEXT_PRICING: LongContextPricing = {
  inputTokenThreshold: 272_000,
  inputCostMultiplier: 2,
  outputCostMultiplier: 1.5,
};

const getLongContextPricingMultipliers = (
  usage: RawBillingQuantityDetails,
  longContextPricing?: LongContextPricing,
): {
  inputCostMultiplier: number;
  outputCostMultiplier: number;
} => {
  const totalInputTokens = usage.inputTokens + usage.cachedInputTokens;
  if (
    longContextPricing == null ||
    totalInputTokens <= longContextPricing.inputTokenThreshold
  ) {
    return { inputCostMultiplier: 1, outputCostMultiplier: 1 };
  }

  return {
    inputCostMultiplier: longContextPricing.inputCostMultiplier,
    outputCostMultiplier: longContextPricing.outputCostMultiplier,
  };
};

const opus46Calculation: CreditsFromUsageFn = (usage) => {
  const { cacheWrites, cachedInputTokens, inputTokens, outputTokens } = usage;
  const totalInputTokens = inputTokens + cachedInputTokens;
  const isLongContext = totalInputTokens > LONG_CONTEXT_THRESHOLD;

  // Opus4.6: Tiered pricing based on prompt size
  // < 200k: input $5/MTok, output $25/MTok
  // > 200k: input $10/MTok, output $37.50/MTok
  const INPUT_COST_PER_MILLION_TOKENS = isLongContext ? 10 : 5;
  const CACHE_WRITES_COST_PER_MILLION_TOKENS = isLongContext ? 20 : 10;
  const CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10 = isLongContext ? 10 : 5;
  // times 4 to avoid fp math: 37.5 * 4 = 150, 25 * 4 = 100
  const OUTPUT_COST_PER_MILLION_TOKENS_TIMES_4 = isLongContext ? 150 : 100;

  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheWritesMillionthCredits =
    cacheWrites * CACHE_WRITES_COST_PER_MILLION_TOKENS;
  const cachedInputMillionthCredits =
    (cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKENS_TIMES_10) / 10;
  const outputMillionthCredits =
    (outputTokens * OUTPUT_COST_PER_MILLION_TOKENS_TIMES_4) / 4;

  return (
    inputMillionthCredits +
    cacheWritesMillionthCredits +
    cachedInputMillionthCredits +
    outputMillionthCredits
  );
};

const openaiGpt41V1Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 3;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.5;
  const OUTPUT_COST_PER_MILLION_TOKENS = 12;
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // as of 08/2025 we don't cache openai
  // gpt4.1@08/2025: Billed at input: $3/MTok, output: $12/MTok, cached input $0.50/MTok
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

const openaiGpt51Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 1.25;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.125;
  const OUTPUT_COST_PER_MILLION_TOKENS = 10;
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // gpt-5.1@11/2025: Billed at input: $1.25/MTok, output: $10/MTok, cached input $0.125/MTok
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

const openaiGpt53CodexCalculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 1.75;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.175;
  const OUTPUT_COST_PER_MILLION_TOKENS = 14;
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // gpt-5.3-codex@02/2026: Billed at input: $1.75/MTok, output: $14/MTok, cached input $0.175/MTok
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

const openaiGpt54Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 2.5;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.25;
  const OUTPUT_COST_PER_MILLION_TOKENS = 15;
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // gpt-5.4@03/2026: Billed at input: $2.50/MTok, output: $15/MTok, cached input $0.25/MTok
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

const openaiGpt55Calculation: CreditsFromUsageFn = (usage) => {
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const { inputCostMultiplier, outputCostMultiplier } =
    getLongContextPricingMultipliers(usage, OPENAI_GPT_55_LONG_CONTEXT_PRICING);

  const INPUT_COST_PER_MILLION_TOKENS = 5 * inputCostMultiplier;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.5 * inputCostMultiplier;
  const OUTPUT_COST_PER_MILLION_TOKENS = 30 * outputCostMultiplier;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // gpt-5.5@12/2025: Billed at input: $5/MTok, output: $30/MTok, cached input $0.50/MTok.
  // Prompts over 272K input tokens are billed at 2x input/cache input and 1.5x output.
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

const openaiGpt54MiniCalculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 0.75;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.075;
  const OUTPUT_COST_PER_MILLION_TOKENS = 4.5;
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // gpt-5.4-mini@03/2026: Billed at input: $0.75/MTok, output: $4.50/MTok, cached input $0.075/MTok
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

const openaiGpt54NanoCalculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 0.2;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.02;
  const OUTPUT_COST_PER_MILLION_TOKENS = 1.25;
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // gpt-5.4-nano@03/2026: Billed at input: $0.20/MTok, output: $1.25/MTok, cached input $0.02/MTok
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

const kimiK26Calculation: CreditsFromUsageFn = (usage) => {
  const INPUT_COST_PER_MILLION_TOKENS = 0.95;
  const CACHED_INPUT_COST_PER_MILLION_TOKEN = 0.16;
  const OUTPUT_COST_PER_MILLION_TOKENS = 4.0;
  const { cachedInputTokens, inputTokens, outputTokens } = usage;
  const inputMillionthCredits = inputTokens * INPUT_COST_PER_MILLION_TOKENS;
  const cacheInputMillionthCredits =
    cachedInputTokens * CACHED_INPUT_COST_PER_MILLION_TOKEN;
  const outputMillionthCredits = outputTokens * OUTPUT_COST_PER_MILLION_TOKENS;

  // Kimi-K2.6@05/2026: Billed at input: $0.95/MTok, output: $4.00/MTok, cached input $0.16/MTok
  return (
    inputMillionthCredits + cacheInputMillionthCredits + outputMillionthCredits
  );
};

// and a user will see a fluctuation in their credit usage mid-billing cycle
export const MODEL_TO_CREDIT_CONVERSION_V1: Record<
  BillableModel,
  CreditsFromUsageFn
> = {
  [DeprecatedAnthropicModel.CLAUDE_SONNET_35]: sonnet4Calculation,
  [DeprecatedAnthropicModel.CLAUDE_3_OPUS]: sonnet4Calculation,
  [DeprecatedAnthropicModel.RESEARCH_MODEL_FIREFLY]: sonnet4Calculation,
  [DeprecatedAnthropicModel.CLAUDE_SONNET_35_NEW]: sonnet4Calculation,
  [DeprecatedAnthropicModel.CLAUDE_SONNET_37]: sonnet4Calculation,
  [DeprecatedAnthropicModel.CLAUDE_4_OPUS]: opus4Calculation,
  [DeprecatedAnthropicModel.CLAUDE_4_SONNET]: sonnet4Calculation,
  [AnthropicModel.CLAUDE_4_5_SONNET]: sonnet4Calculation,
  [AnthropicModel.CLAUDE_4_5_HAIKU]: haiku45Calculation,
  [DeprecatedAnthropicModel.CLAUDE_4_5_OPUS]: opus45Calculation,
  [AnthropicModel.CLAUDE_4_6_OPUS]: opus46Calculation,
  [AnthropicModel.CLAUDE_4_7_OPUS]: opus45Calculation,
  [DeprecatedAnthropicModel.CLAUDE_NOUGAT_EAP]: opus45Calculation,
  [DeprecatedAnthropicModel.CLAUDE_FUDGE_EAP]: opus46Calculation,
  [DeprecatedAnthropicModel.CLAUDE_CHURRO_EAP]: sonnet4Calculation,
  [AnthropicModel.CLAUDE_4_6_SONNET]: sonnet4Calculation,
  [DeprecatedOpenAiChatModel.GPT_4_OMNI]: openaiGpt41V1Calculation,
  [DeprecatedOpenAiChatModel.GPT_4_OMNI_MINI]: openaiGpt41V1Calculation,
  [OpenAiChatModel.GPT_41]: openaiGpt41V1Calculation,
  [DeprecatedOpenAiChatModel.GPT_41_MINI]: openaiGpt41V1Calculation,
  [DeprecatedOpenAiChatModel.GPT_41_NANO]: openaiGpt41V1Calculation,
  [DeprecatedOpenAiChatModel.O3]: openaiGpt41V1Calculation,
  [DeprecatedOpenAiChatModel.GPT_5]: openaiGpt41V1Calculation,
  [DeprecatedOpenAiChatModel.GPT_51]: openaiGpt51Calculation,
  [DeprecatedOpenAiChatModel.GPT_52]: openaiGpt51Calculation, // Same pricing as 5.1 for now
  [DeprecatedOpenAiChatModel.GPT_53_CODEX]: openaiGpt53CodexCalculation,
  [OpenAiChatModel.GPT_54]: openaiGpt54Calculation,
  [OpenAiChatModel.GPT_54_MINI]: openaiGpt54MiniCalculation,
  [DeprecatedOpenAiChatModel.GPT_54_NANO]: openaiGpt54NanoCalculation,
  [OpenAiChatModel.CREST_ALPHA]: openaiGpt54Calculation,
  [OpenAiChatModel.GPT_55]: openaiGpt55Calculation,
  [KimiModel.KIMI_K_2_6]: kimiK26Calculation,
  [KimiModel.KIMI_K_2_6_THINKING]: kimiK26Calculation,
};

export const getCreditsFromUsageV1 = (
  usage: RawBillingQuantityDetails,
  model: BillableModel,
): MillionthCredits => {
  const creditsFromUsageFn = MODEL_TO_CREDIT_CONVERSION_V1[model];
  return creditsFromUsageFn(usage);
};

/** USD cents for one automated AI credit top-up invoice (~$25). */
export const AI_CREDIT_TOP_UP_AMOUNT_CENTS = 2500;

/** Credits in one standard auto top-up pack (user-facing). Keep aligned with `AI_CREDIT_TOP_UP_AMOUNT_CENTS` and `USER_CREDIT_VALUE_CENTS`. */
export const AI_CREDIT_TOP_UP_CREDIT_COUNT = 50;

/**
 * This is the total value we charge a user per-credit.
 * We need to track this here because we build a margin into the
 * cost per-million tokens and so need an anchor point to translate
 * tokens into credits.
 */
export const USER_CREDIT_VALUE_CENTS = 50;

export const FALLBACK_PRICING_MODEL: BillableModel =
  AnthropicModel.CLAUDE_4_6_SONNET;

export interface ModelPricingTable {
  costInCentsPerMilTokens: {
    inputTokens: number;
    outputTokens: number;
    cacheWrites: number;
    cacheReads: number;
  };
  marginPercent: number;
  longContextPricing?: LongContextPricing;
}

export const PRICING_BY_MODEL: Partial<
  Record<BillableModel, ModelPricingTable>
> = {
  [AnthropicModel.CLAUDE_4_6_SONNET]: {
    costInCentsPerMilTokens: {
      inputTokens: 300,
      outputTokens: 1500,
      cacheWrites: 375,
      cacheReads: 30,
    },
    marginPercent: 0.4,
  },
  [AnthropicModel.CLAUDE_4_5_SONNET]: {
    costInCentsPerMilTokens: {
      inputTokens: 300,
      outputTokens: 1500,
      cacheWrites: 375,
      cacheReads: 30,
    },
    marginPercent: 0.4,
  },
  [DeprecatedAnthropicModel.CLAUDE_4_SONNET]: {
    costInCentsPerMilTokens: {
      inputTokens: 300,
      outputTokens: 1500,
      cacheWrites: 375,
      cacheReads: 30,
    },
    marginPercent: 0.4,
  },
  [AnthropicModel.CLAUDE_4_6_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [AnthropicModel.CLAUDE_4_7_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [DeprecatedAnthropicModel.CLAUDE_NOUGAT_EAP]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [DeprecatedAnthropicModel.CLAUDE_4_5_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [DeprecatedAnthropicModel.CLAUDE_4_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 1500,
      outputTokens: 7500,
      cacheWrites: 1875,
      cacheReads: 150,
    },
    marginPercent: 0.05,
  },
  [AnthropicModel.CLAUDE_4_5_HAIKU]: {
    costInCentsPerMilTokens: {
      inputTokens: 100,
      outputTokens: 500,
      cacheWrites: 125,
      cacheReads: 10,
    },
    marginPercent: 0.45,
  },
  [OpenAiChatModel.GPT_54]: {
    costInCentsPerMilTokens: {
      inputTokens: 250,
      outputTokens: 1500,
      cacheWrites: 0,
      cacheReads: 25,
    },
    marginPercent: 0.45,
  },
  [OpenAiChatModel.GPT_55]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 3000,
      cacheWrites: 0,
      cacheReads: 50,
    },
    marginPercent: 0.45,
    longContextPricing: OPENAI_GPT_55_LONG_CONTEXT_PRICING,
  },
  [OpenAiChatModel.CREST_ALPHA]: {
    costInCentsPerMilTokens: {
      inputTokens: 250,
      outputTokens: 1500,
      cacheWrites: 0,
      cacheReads: 25,
    },
    marginPercent: 0.45,
  },
  [DeprecatedOpenAiChatModel.GPT_52]: {
    costInCentsPerMilTokens: {
      inputTokens: 175,
      outputTokens: 1400,
      cacheWrites: 0,
      cacheReads: 17.5,
    },
    marginPercent: 0.45,
  },
  [OpenAiChatModel.GPT_41]: {
    costInCentsPerMilTokens: {
      inputTokens: 200,
      outputTokens: 800,
      cacheWrites: 0,
      cacheReads: 50,
    },
    marginPercent: 0.45,
  },
  [KimiModel.KIMI_K_2_6]: {
    costInCentsPerMilTokens: {
      inputTokens: 95,
      outputTokens: 400,
      cacheWrites: 0,
      cacheReads: 16,
    },
    marginPercent: 0.6,
  },
  [KimiModel.KIMI_K_2_6_THINKING]: {
    costInCentsPerMilTokens: {
      inputTokens: 95,
      outputTokens: 400,
      cacheWrites: 0,
      cacheReads: 16,
    },
    marginPercent: 0.6,
  },
};

const calculateMillionthCreditsPerTokenCoefficient = (
  costInCentsPerMilTokens: number,
  marginPercent: number,
): MillionthCredits => {
  const userCostPerMilTokens = costInCentsPerMilTokens / (1 - marginPercent);
  const creditsPerMilTokens = userCostPerMilTokens / USER_CREDIT_VALUE_CENTS;
  // Millionth-credit-per-token is the same as credits-per-million-tokens
  return creditsPerMilTokens;
};

export const getPricingTableForUsage = ({
  priceTable,
  usage,
}: {
  priceTable: ModelPricingTable;
  usage: RawBillingQuantityDetails;
}): ModelPricingTable => {
  const { inputCostMultiplier, outputCostMultiplier } =
    getLongContextPricingMultipliers(usage, priceTable.longContextPricing);
  if (inputCostMultiplier === 1 && outputCostMultiplier === 1) {
    return priceTable;
  }

  return {
    ...priceTable,
    costInCentsPerMilTokens: {
      ...priceTable.costInCentsPerMilTokens,
      inputTokens:
        priceTable.costInCentsPerMilTokens.inputTokens * inputCostMultiplier,
      cacheWrites:
        priceTable.costInCentsPerMilTokens.cacheWrites * inputCostMultiplier,
      cacheReads:
        priceTable.costInCentsPerMilTokens.cacheReads * inputCostMultiplier,
      outputTokens:
        priceTable.costInCentsPerMilTokens.outputTokens * outputCostMultiplier,
    },
  };
};

export const getCreditsFromUsageV2 = (
  usage: RawBillingQuantityDetails,
  model: BillableModel,
): MillionthCredits => {
  let priceTable: ModelPricingTable;
  if (PRICING_BY_MODEL[model] != null) {
    priceTable = PRICING_BY_MODEL[model];
  } else {
    priceTable = PRICING_BY_MODEL[FALLBACK_PRICING_MODEL]!;
  }
  priceTable = getPricingTableForUsage({ priceTable, usage });

  const inputTokenCost =
    usage.inputTokens *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.inputTokens,
      priceTable.marginPercent,
    );

  const outputTokenCost =
    usage.outputTokens *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.outputTokens,
      priceTable.marginPercent,
    );

  const cacheWriteCost =
    usage.cacheWrites *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.cacheWrites,
      priceTable.marginPercent,
    );

  const cacheReadsCost =
    usage.cachedInputTokens *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.cacheReads,
      priceTable.marginPercent,
    );

  // We don't care about billing for fractional ten-millionth credits.
  // Additionally, from this point it's easier for us if this number is an integer.
  return Math.floor(
    inputTokenCost + outputTokenCost + cacheWriteCost + cacheReadsCost,
  );
};
